// src/components/ModalNovoFornecedor.jsx
import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';

export default function ModalNovoFornecedor({
  isOpen,
  onClose,
  onSave,
  fornecedorEmEdicao,
}) {
  const DEFAULT_FORM = { nome: '', telefone: '', contatoNome: '', obs: '' };
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setForm({ ...DEFAULT_FORM, ...(fornecedorEmEdicao || {}) });
      setErrors({});
    }
  }, [isOpen, fornecedorEmEdicao]);

  if (!isOpen) return null;

  const set = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'telefone') {
      return set(name, (value || '').replace(/\D/g, ''));
    }
    set(name, value);
  };

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = 'Nome é obrigatório';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSaveClick = (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    const payload = {
      nome: form.nome.trim(),
      telefone: (form.telefone || '').trim(),
      contatoNome: (form.contatoNome || '').trim(),
      obs: (form.obs || '').trim(),
    };
    onSave(payload);
  };

  const handleBackdrop = (evt) => {
    if (evt.target === evt.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein"
      role="dialog"
      aria-modal="true"
      onClick={handleBackdrop}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-xl relative animate-modalSlideIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[94vh] flex flex-col">
          {/* Header */}
          <div className="p-7 pb-0 relative">
            <button
              className="absolute right-8 top-7 text-gray-500 hover:text-blue-700 dark:text-gray-200 dark:hover:text-white text-base font-medium"
              onClick={onClose}
            >
              Fechar
            </button>
            <h2 className="text-2xl font-bold mb-6 text-blue-600 dark:text-white">
              {fornecedorEmEdicao ? 'Editar Fornecedor' : 'Novo Fornecedor'}
            </h2>
          </div>

          {/* Body */}
          <form
            onSubmit={handleSaveClick}
            className="px-7 pb-6 space-y-5 overflow-y-auto flex-1"
          >
            <InputField
              label="Nome do Fornecedor"
              name="nome"
              value={form.nome}
              onChange={handleChange}
              placeholder="Ex: Auto Peças Silva"
              required
              error={errors.nome}
            />

            <InputField
              label="Telefone (apenas números)"
              name="telefone"
              value={form.telefone}
              onChange={handleChange}
              inputMode="numeric"
              placeholder="Ex: 16999999999"
            />

            <InputField
              label="Nome do Vendedor/Contato"
              name="contatoNome"
              value={form.contatoNome}
              onChange={handleChange}
              placeholder="Ex: João"
            />

            <div>
              <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
                Observações / Principais Produtos
              </label>
              <textarea
                name="obs"
                value={form.obs}
                onChange={handleChange}
                rows={4}
                className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
                placeholder="Ex: Filtros de óleo, correias, etc."
              />
            </div>
          </form>

          {/* Footer — apenas um botão, full-width */}
          <div className="px-7 pb-6">
            <button
              onClick={handleSaveClick}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              type="button"
            >
              <Save size={18} />
              {fornecedorEmEdicao ? 'Salvar Alterações' : 'Cadastrar Fornecedor'}
            </button>
          </div>
        </div>

        {/* Animações no padrão ModalNovaOS */}
        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97); }
            100% { opacity:1; transform: translateY(0) scale(1); }
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
}

/* ------- Componentes auxiliares estilizados ------- */
const InputField = ({ label, error, required, ...props }) => (
  <div>
    <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      {...props}
      className={`border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition ${
        error ? 'border-red-500' : ''
      }`}
    />
    {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
  </div>
);
